import React, { useEffect, useMemo, useRef, useState } from "react";
import { ImagePlus, Loader2, Plus, X } from "lucide-react";
import { getAccessToken } from "../../../../awsAuth";
import "./BusinessModuleCreate.scss";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

const MODULE_CREATE_CONFIG = {
  products: {
    title: "Додати товар",
    editTitle: "Редагувати товар",
    subtitle: "Створи товар для каталогу бізнес-профілю.",
    submitLabel: "Зберегти товар",
    editSubmitLabel: "Зберегти зміни",
    successLabel: "Товар створено",
    endpoint: "/api/business/products/me",
    patchBase: "/api/business/products",
    titlePlaceholder: "Назва товару *",
    descriptionPlaceholder: "Опис товару",
    pricePlaceholder: "Ціна",
    showPrice: true,
    showCurrency: true,
    showCondition: true,
    showAvailability: true,
    showPhotos: true,
    phoneLabel: "Показувати телефон у товарі",
  },

  services: {
    title: "Додати послугу",
    editTitle: "Редагувати послугу",
    subtitle: "Створи послугу для бізнес-профілю.",
    submitLabel: "Зберегти послугу",
    editSubmitLabel: "Зберегти зміни",
    successLabel: "Послугу створено",
    endpoint: "/api/business/services/me",
    patchBase: "/api/business/services",
    titlePlaceholder: "Назва послуги *",
    descriptionPlaceholder: "Опис послуги",
    pricePlaceholder: "Ціна",
    durationPlaceholder: "Тривалість, наприклад 60 хв",
    showPrice: true,
    showCurrency: true,
    showDuration: true,
    showPhotos: true,
    phoneLabel: "Показувати телефон у послузі",
  },

  vacancies: {
    title: "Додати вакансію",
    subtitle: "Створи вакансію для бізнес-профілю, стрічки та пошуку.",
    submitLabel: "Опублікувати вакансію",
    successLabel: "Вакансію створено",
    endpoint: "/api/business/vacancies/me",
    titlePlaceholder: "Назва вакансії *",
    descriptionPlaceholder: "Опис вакансії, обовʼязки, умови",
    pricePlaceholder: "Зарплата від",
    showPrice: true,
    showCurrency: true,
    showPhotos: true,
    showDuration: false,
    showCondition: false,
    showAvailability: false,
    phoneLabel: "Показувати телефон у вакансії",
  },
};

export default function BusinessModuleCreate({
  open,
  moduleType = "products",
  editItem = null,
  onClose,
  onSubmit,
}) {
  const fileInputRef = useRef(null);
  const isEditing = !!editItem;

  const config = useMemo(() => {
    return MODULE_CREATE_CONFIG[moduleType] || MODULE_CREATE_CONFIG.products;
  }, [moduleType]);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("ГРН");
  const [duration, setDuration] = useState("");
  const [condition, setCondition] = useState("na");
  const [availability, setAvailability] = useState("available");
  const [photos, setPhotos] = useState([]);
  const [showPhone, setShowPhone] = useState(false);
  const [contactPhone, setContactPhone] = useState("");

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Prefill from the existing item when opened for edit; blank slate when
  // opened for create. Re-runs whenever the modal opens or which item it's
  // editing changes, so a fresh open always reflects the current item, not
  // whatever was left over from a previous edit/create session.
  useEffect(() => {
    if (!open) return;

    if (editItem) {
      setTitle(editItem.title || "");
      setDescription(editItem.description || "");
      setPrice(editItem.price ? String(editItem.price) : "");
      setCurrency(editItem.currency || "ГРН");
      setDuration(editItem.duration || "");
      setCondition(editItem.condition || "na");
      setAvailability(editItem.availability || "available");
      setPhotos(
        Array.isArray(editItem.photos)
          ? editItem.photos
              .filter(Boolean)
              .map((url, i) => ({ id: `existing_${i}_${url}`, url, preview: url }))
          : []
      );
      setShowPhone(!!editItem.showPhone);
      setContactPhone(editItem.contactPhone || "");
    } else {
      setTitle("");
      setDescription("");
      setPrice("");
      setCurrency("ГРН");
      setDuration("");
      setCondition("na");
      setAvailability("available");
      setPhotos([]);
      setShowPhone(false);
      setContactPhone("");
    }

    setError("");
  }, [open, editItem]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setPrice("");
    setCurrency("ГРН");
    setDuration("");
    setCondition("na");
    setAvailability("available");
    setPhotos([]);
    setShowPhone(false);
    setContactPhone("");
    setUploadingPhoto(false);
    setSaving(false);
    setError("");
  };

  const handleClose = () => {
    if (saving || uploadingPhoto) return;
    resetForm();
    onClose?.();
  };

  const uploadSinglePhoto = async (file) => {
    const formData = new FormData();
    formData.append("photo", file);

    const res = await fetch(`${API_BASE}/api/listings/upload-photo`, {
      method: "POST",
      body: formData,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data?.error || "Не вдалося завантажити фото");
    }

    return data.photoUrl;
  };

  const handlePhotoPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const freeSlots = Math.max(0, 6 - photos.length);
    const selected = files.slice(0, freeSlots);

    if (!selected.length) {
      e.target.value = "";
      return;
    }

    try {
      setUploadingPhoto(true);
      setError("");

      const uploaded = await Promise.all(
        selected.map(async (file) => {
          const preview = URL.createObjectURL(file);
          const url = await uploadSinglePhoto(file);

          return {
            id: `${file.name}_${Date.now()}_${Math.random()}`,
            url,
            preview,
          };
        })
      );

      setPhotos((prev) => [...prev, ...uploaded]);
    } catch (err) {
      console.error(err);
      setError(err.message || "Не вдалося завантажити фото");
    } finally {
      setUploadingPhoto(false);
      if (e.target) e.target.value = "";
    }
  };

  const removePhoto = (id) => {
    if (saving || uploadingPhoto) return;
    setPhotos((prev) => prev.filter((photo) => photo.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (saving || uploadingPhoto) return;

    if (!title.trim()) {
      setError("Назва обовʼязкова.");
      return;
    }

    if (config.showPhotos && photos.length === 0) {
      setError("Додай хоча б одне фото.");
      return;
    }

    if (!config.endpoint) {
      setError("Для цього модуля бекенд ще не підключений.");
      return;
    }

    if (isEditing && !config.patchBase) {
      setError("Редагування цього типу поки не підтримується.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const token = await getAccessToken();

      const payload = {
        title: title.trim(),
        description: description.trim(),
        price: price ? Number(price) : 0,
        currency,
        photos: photos.map((photo) => photo.url),
        condition,
        availability,
        showPhone,
        contactPhone: contactPhone.trim(),
      };

      if (duration.trim()) {
        payload.duration = duration.trim();
      }

      const url = isEditing
        ? `${API_BASE}${config.patchBase}/${encodeURIComponent(editItem.listingId)}`
        : `${API_BASE}${config.endpoint}`;

      const res = await fetch(url, {
        method: isEditing ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося зберегти");
      }

      onSubmit?.(data.product || data.service || data.vacancy || data.item || data);
      resetForm();
      onClose?.();
    } catch (err) {
      console.error(err);
      setError(err.message || "Помилка збереження");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="bmCreate">
      <button
        type="button"
        className="bmCreate__backdrop"
        onClick={handleClose}
        aria-label="Закрити створення"
      />

      <section className="bmCreate__panel bmCreate__panel--wide">
        <div className="bmCreate__handle" />

        <div className="bmCreate__head">
          <div>
            <h2>{isEditing ? config.editTitle || config.title : config.title}</h2>
            <p>{config.subtitle}</p>
          </div>

          <button type="button" onClick={handleClose} aria-label="Закрити">
            <X size={19} strokeWidth={2.7} />
          </button>
        </div>

        {error && <div className="bmCreate__error">{error}</div>}

        <form className="bmCreate__form" onSubmit={handleSubmit}>
          {config.showPhotos && (
            <div className="bmCreatePhotos">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoPick}
              />

              <div className="bmCreatePhotos__grid">
                {photos.map((photo, index) => (
                  <div key={photo.id} className="bmCreatePhotos__tile">
                    <img src={photo.preview || photo.url} alt="" />

                    {index === 0 && (
                      <span className="bmCreatePhotos__main">Головне</span>
                    )}

                    <button
                      type="button"
                      onClick={() => removePhoto(photo.id)}
                      aria-label="Видалити фото"
                    >
                      <X size={15} strokeWidth={2.8} />
                    </button>
                  </div>
                ))}

                {photos.length < 6 && (
                  <button
                    type="button"
                    className="bmCreatePhotos__add"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto || saving}
                  >
                    {uploadingPhoto ? (
                      <Loader2 className="bmCreate__spin" size={24} />
                    ) : (
                      <ImagePlus size={24} strokeWidth={2.4} />
                    )}
                    <span>{uploadingPhoto ? "Завантаження..." : "Додати фото"}</span>
                  </button>
                )}
              </div>
            </div>
          )}

          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={config.titlePlaceholder}
            maxLength={90}
            disabled={saving}
          />

          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={config.descriptionPlaceholder}
            maxLength={1600}
            disabled={saving}
          />

          <div className="bmCreate__row">
            {config.showPrice && (
              <input
                type="number"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={config.pricePlaceholder}
                disabled={saving}
              />
            )}

            {config.showCurrency && (
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={saving}
              >
                <option value="ГРН">ГРН</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            )}
          </div>

          {config.showDuration && (
            <input
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder={config.durationPlaceholder}
              maxLength={80}
              disabled={saving}
            />
          )}

          {config.showCondition && (
            <div className="bmCreate__segmented">
              <button
                type="button"
                className={condition === "na" ? "is-active" : ""}
                onClick={() => setCondition("na")}
              >
                Не вказано
              </button>

              <button
                type="button"
                className={condition === "new" ? "is-active" : ""}
                onClick={() => setCondition("new")}
              >
                Нове
              </button>

              <button
                type="button"
                className={condition === "used" ? "is-active" : ""}
                onClick={() => setCondition("used")}
              >
                Вживане
              </button>
            </div>
          )}

          {config.showAvailability && (
            <select
              value={availability}
              onChange={(e) => setAvailability(e.target.value)}
              disabled={saving}
            >
              <option value="available">В наявності</option>
              <option value="preorder">Під замовлення</option>
              <option value="out_of_stock">Немає в наявності</option>
            </select>
          )}

          <label className="bmCreate__check">
            <input
              type="checkbox"
              checked={showPhone}
              onChange={(e) => setShowPhone(e.target.checked)}
              disabled={saving}
            />
            {config.phoneLabel || "Показувати телефон"}
          </label>

          {showPhone && (
            <input
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+380..."
              maxLength={40}
              disabled={saving}
            />
          )}

          <button
            type="submit"
            className="bmCreate__submit"
            disabled={saving || uploadingPhoto}
          >
            {saving ? (
              <Loader2 className="bmCreate__spin" size={19} />
            ) : (
              <Plus size={19} strokeWidth={2.8} />
            )}

            {saving
              ? "Збереження..."
              : isEditing
              ? config.editSubmitLabel || config.submitLabel
              : config.submitLabel}
          </button>
        </form>
      </section>
    </div>
  );
}